import { sha256 } from "@noble/hashes/sha2.js";
import { ed25519 } from "@noble/curves/ed25519.js";
import { Buffer } from "buffer";

if (typeof globalThis.Buffer === "undefined") {
  (globalThis as { Buffer?: typeof Buffer }).Buffer = Buffer;
}

class Sha256Hash {
  private chunks: Uint8Array[] = [];

  update(input: Uint8Array | string | Buffer): this {
    if (typeof input === "string") {
      this.chunks.push(new TextEncoder().encode(input));
    } else if (input instanceof Uint8Array) {
      this.chunks.push(input);
    } else {
      this.chunks.push(new Uint8Array(input as ArrayBufferLike));
    }
    return this;
  }

  digest(encoding?: "hex" | "base64"): string | Uint8Array {
    const total = this.chunks.reduce((n, c) => n + c.length, 0);
    const buf = new Uint8Array(total);
    let offset = 0;
    for (const c of this.chunks) {
      buf.set(c, offset);
      offset += c.length;
    }
    const digest = sha256(buf);
    if (encoding === "hex") return Buffer.from(digest).toString("hex");
    if (encoding === "base64") return Buffer.from(digest).toString("base64");
    return digest;
  }
}

export function createHash(algorithm: string): Sha256Hash {
  if (algorithm !== "sha256") {
    throw new Error(`Browser crypto shim only supports sha256, got ${algorithm}`);
  }
  return new Sha256Hash();
}

function stripPem(pem: string): Uint8Array {
  const body = pem
    .replace(/-----BEGIN [^-]+-----/g, "")
    .replace(/-----END [^-]+-----/g, "")
    .replace(/[\s\r\n]/g, "");
  return new Uint8Array(Buffer.from(body, "base64"));
}

// Fixed ASN.1 prefixes for Ed25519 (OID 1.3.101.112, the 2b 65 70
// triplet). The SPKI prefix is 12 bytes preceding the 32-byte public key
// (44-byte DER); the PKCS8 prefix is 16 bytes preceding the 32-byte
// private key seed (48-byte DER). These match the constants in
// apps/dashboard/src/lib/pem.ts, which round-trip byte-for-byte against
// Node's crypto export.
const SPKI_ED25519_PREFIX = Uint8Array.from([
  0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00
]);
const PKCS8_ED25519_PREFIX = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20
]);

function hasPrefix(der: Uint8Array, prefix: Uint8Array): boolean {
  for (let i = 0; i < prefix.length; i++) {
    if (der[i] !== prefix[i]) return false;
  }
  return true;
}

// Validate that DER bytes are a well-formed Ed25519 SPKI public key
// before the shim claims asymmetricKeyType "ed25519". Without this, the
// shim would take the last 32 bytes of any >=32-byte DER as a raw key
// and label a non-Ed25519 (or malformed) PEM as Ed25519, letting
// src/lib/keys.ts trust a key real Node would reject or classify
// differently. The length and prefix are fixed for Ed25519, so an exact
// match is the correct gate.
function isEd25519Spki(spki: Uint8Array): boolean {
  return spki.length === SPKI_ED25519_PREFIX.length + 32 && hasPrefix(spki, SPKI_ED25519_PREFIX);
}

function isEd25519Pkcs8(pkcs8: Uint8Array): boolean {
  return (
    pkcs8.length === PKCS8_ED25519_PREFIX.length + 32 && hasPrefix(pkcs8, PKCS8_ED25519_PREFIX)
  );
}

function extractEd25519PublicKeyFromSpki(spki: Uint8Array): Uint8Array {
  if (spki.length < 32) {
    throw new Error("SPKI too short to contain Ed25519 public key");
  }
  return spki.slice(spki.length - 32);
}

function extractEd25519PrivateKeyFromPkcs8(pkcs8: Uint8Array): Uint8Array {
  if (pkcs8.length < 32) {
    throw new Error("PKCS8 too short to contain Ed25519 private key");
  }
  return pkcs8.slice(pkcs8.length - 32);
}

export interface PublicKeyObject {
  __sequesignKind: "public";
  raw: Uint8Array;
  pem: string;
  asymmetricKeyType: "ed25519";
  export(options: { type: "spki"; format: "der" | "pem" }): Buffer | string;
}

export interface PrivateKeyObject {
  __sequesignKind: "private";
  raw: Uint8Array;
  pem: string;
  asymmetricKeyType: "ed25519";
  export(options: { type: "pkcs8"; format: "der" | "pem" }): Buffer | string;
}

export function createPublicKey(input: string | PublicKeyObject): PublicKeyObject {
  if (typeof input === "object" && input.__sequesignKind === "public") return input;
  const pem = typeof input === "string" ? input : (input as { pem: string }).pem;
  const spki = stripPem(pem);
  // Reject non-Ed25519 / malformed keys before labeling this object
  // asymmetricKeyType "ed25519". Real Node createPublicKey would not
  // classify these as Ed25519; throwing routes the verifier through
  // parseEd25519PublicKeyPem's catch and the protocol-correct
  // agent_identity_attestation_fingerprint_mismatch failure.
  if (!isEd25519Spki(spki)) {
    throw new Error("unsupported key type: expected an Ed25519 SPKI public key");
  }
  const raw = extractEd25519PublicKeyFromSpki(spki);
  return {
    __sequesignKind: "public",
    raw,
    pem,
    asymmetricKeyType: "ed25519",
    export({ type, format }) {
      if (type !== "spki") {
        throw new Error(`Browser crypto shim only supports spki export, got ${type}`);
      }
      if (format === "pem") return pem;
      return Buffer.from(spki);
    }
  };
}

export function createPrivateKey(input: string | PrivateKeyObject): PrivateKeyObject {
  if (typeof input === "object" && input.__sequesignKind === "private") return input;
  const pem = typeof input === "string" ? input : (input as { pem: string }).pem;
  const pkcs8 = stripPem(pem);
  // Same Ed25519 gate as createPublicKey, for PKCS8 private keys.
  if (!isEd25519Pkcs8(pkcs8)) {
    throw new Error("unsupported key type: expected an Ed25519 PKCS8 private key");
  }
  const raw = extractEd25519PrivateKeyFromPkcs8(pkcs8);
  return {
    __sequesignKind: "private",
    raw,
    pem,
    asymmetricKeyType: "ed25519",
    export({ type, format }) {
      if (type !== "pkcs8") {
        throw new Error(`Browser crypto shim only supports pkcs8 export, got ${type}`);
      }
      if (format === "pem") return pem;
      return Buffer.from(pkcs8);
    }
  };
}

function toBytes(input: Uint8Array | Buffer | string): Uint8Array {
  if (typeof input === "string") return new TextEncoder().encode(input);
  if (input instanceof Uint8Array) return input;
  return new Uint8Array(input as ArrayBufferLike);
}

export function verify(
  algorithm: null,
  data: Uint8Array | Buffer | string,
  key: string | PublicKeyObject,
  signature: Uint8Array | Buffer
): boolean {
  if (algorithm !== null) {
    throw new Error(`Browser crypto shim only supports Ed25519 verify with algorithm=null.`);
  }
  try {
    const pub = createPublicKey(key);
    const message = toBytes(data);
    const sig = signature instanceof Uint8Array ? signature : new Uint8Array(signature);
    return ed25519.verify(sig, message, pub.raw);
  } catch {
    return false;
  }
}

export function sign(
  algorithm: null,
  data: Uint8Array | Buffer | string,
  key: string | PrivateKeyObject
): Buffer {
  if (algorithm !== null) {
    throw new Error(`Browser crypto shim only supports Ed25519 sign with algorithm=null.`);
  }
  const priv = createPrivateKey(key);
  const message = toBytes(data);
  const sig = ed25519.sign(message, priv.raw);
  return Buffer.from(sig);
}

export function generateKeyPairSync(_kind: "ed25519"): never {
  throw new Error("generateKeyPairSync is not available in the browser verifier shim.");
}

export default {
  createHash,
  createPublicKey,
  createPrivateKey,
  verify,
  sign,
  generateKeyPairSync
};
